import { useApolloClient, useMutation } from "@apollo/client";
import {
  Button,
  Center,
  FormControl,
  FormErrorMessage,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import {
  AdminCreateTagCategoryMutation,
  AdminCreateTagCategoryMutationVariables,
  AdminEditTagCategoryMutation,
  AdminEditTagCategoryMutationVariables,
  TagListQuery,
} from "gql/graphql";
import { useTags } from "modules/providers/tags-provider";
import TAG_LIST_QUERY from "modules/providers/tags-provider/networking/tag-list";
import React, { memo, useCallback, useState } from "react";
import { FieldValues, useForm } from "react-hook-form";
import colors from "app/colors";
import { SERVER_ERROR } from "modules/utils/Constants";
import { getMessageFromError } from "modules/utils/Functions";
import ADMIN_CREATE_TAG_CATEGORY_MUTATION from "./_networking/admin-create-tag-category";
import ADMIN_EDIT_TAG_CATEGORY_MUTATION from "./_networking/admin-edit-tag-category";
import { useOverlay } from "modules/providers/overlay-provider";
import { MODAL_TYPE } from "modules/providers/overlay-provider/overlay-provider";
import { GreenCheckIcon } from "modules/icons";

interface IProps {
  tagCategoryId?: string;
}

const AdminTagCategoryModal = ({ tagCategoryId }: IProps) => {
  const { closeModal, openModal } = useOverlay();
  const { tagCategoriesById } = useTags();
  const { cache } = useApolloClient();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [adminEditTagCategoryMutationAPI] = useMutation<
    AdminEditTagCategoryMutation,
    AdminEditTagCategoryMutationVariables
  >(ADMIN_EDIT_TAG_CATEGORY_MUTATION);

  const [adminCreateTagCategoryMutationAPI] = useMutation<
    AdminCreateTagCategoryMutation,
    AdminCreateTagCategoryMutationVariables
  >(ADMIN_CREATE_TAG_CATEGORY_MUTATION);

  const tagCategory = tagCategoryId
    ? tagCategoriesById[tagCategoryId]
    : undefined;

  const {
    handleSubmit,
    formState: { errors },
    register,
    setError,
    clearErrors,
  } = useForm({
    mode: "onSubmit",
    defaultValues: {
      SERVER_ERROR: "",
      name: tagCategory ? tagCategory.name : "",
    },
  });

  const onSubmit = useCallback(
    async ({ name }: FieldValues) => {
      setIsSubmitting(true);
      if (tagCategory) {
        adminEditTagCategoryMutationAPI({
          variables: {
            id: tagCategory.id,
            input: {
              name,
            },
          },
          onError: (error: Error) => {
            setIsSubmitting(false);
            setError(SERVER_ERROR, {
              message: error.message,
            });
          },
          onCompleted: () => {
            closeModal();
            openModal({
              type: MODAL_TYPE.HELP,
              helpModal: {
                icon: <GreenCheckIcon boxSize={100} />,
                title: "Category Edited",
                message: `Tag category, "${tagCategory.name}" has been edited!`,
              },
            });
          },
        });
      } else {
        adminCreateTagCategoryMutationAPI({
          variables: {
            input: {
              name,
            },
          },
          onCompleted: (data) => {
            const tagCategory = data.adminCreateTagCategory;
            if (!tagCategory) {
              return;
            }
            cache.updateQuery<TagListQuery>(
              {
                query: TAG_LIST_QUERY,
              },
              (data) => {
                if (!data) {
                  return data;
                }
                return {
                  ...data,
                  tagCategoryList: [...data.tagCategoryList, tagCategory],
                };
              },
            );
            closeModal();
            openModal({
              type: MODAL_TYPE.HELP,
              helpModal: {
                icon: <GreenCheckIcon boxSize={100} />,
                title: "Category Added",
                message: `Tag category, "${tagCategory.name}" has been created!`,
              },
            });
          },
          onError: (error: Error) => {
            setIsSubmitting(false);
            setError(SERVER_ERROR, {
              message: error.message,
            });
          },
        });
      }
    },
    [
      adminCreateTagCategoryMutationAPI,
      adminEditTagCategoryMutationAPI,
      cache,
      closeModal,
      openModal,
      setError,
      tagCategory,
    ],
  );

  return (
    <Modal isOpen onClose={closeModal} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent borderRadius={16}>
        <ModalHeader>
          <Center>
            <Text fontFamily="Outfit" fontWeight={700} fontSize={26}>
              {tagCategory ? "Edit Tag Category" : "Add Tag Category"}
            </Text>
          </Center>
        </ModalHeader>
        <ModalBody>
          <form id="admin-tag -form" onSubmit={handleSubmit(onSubmit)}>
            <VStack alignItems="flex-start" spacing={4}>
              <FormControl isInvalid={!!errors?.name}>
                <Input
                  id="name"
                  defaultValue={tagCategory ? tagCategory.name : undefined}
                  placeholder="Create unique category name"
                  {...register("name", {
                    required: {
                      message: "a name is required",
                      value: true,
                    },
                  })}
                />
                <FormErrorMessage>
                  {getMessageFromError(errors?.name?.message)}
                </FormErrorMessage>
              </FormControl>
            </VStack>
            {errors.SERVER_ERROR && (
              <Text color={colors.red}>
                {getMessageFromError(errors?.SERVER_ERROR?.message)}
              </Text>
            )}
            <Center pt={5}>
              <HStack>
                <Button
                  variant="cancel"
                  height={8}
                  w={28}
                  onClick={(e) => {
                    e.stopPropagation();
                    closeModal();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  height={8}
                  w={28}
                  onClick={() => {
                    clearErrors([SERVER_ERROR]);
                  }}
                  type="submit"
                >
                  {isSubmitting ? <Spinner /> : "Save"}
                </Button>
              </HStack>
            </Center>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default memo(AdminTagCategoryModal);
